/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  EAreaPrefix,
  IParticipantForUser,
  IPartyForUser,
  StreamType,
  TAreaKey,
  TAvailableActions,
  TModeratorActionType,
  TUserId,
} from '../../PartyClientResponse';
import React, { useState } from 'react';
import { RightOutlined, LeftOutlined } from '@ant-design/icons';
import { Menu, Dropdown, Button, Tooltip, Spin } from 'antd';
import PartyMessages from '../PartyMessages';
import ParticipantActions from './ParticipantActions';
import { useAppState, ActionType } from '../../state/AppState';
import { LoadingOutlined, MoreOutlined } from '@ant-design/icons';
import { gql, useMutation } from '@apollo/client';
import DirectMessages from '../DirectMessages';
import _ from 'lodash';

type TMode = 'chat' | 'guests' | 'directmessages';

const BoxHeader = ({
  title,
  mode,
  setMode,
  children,
}: {
  title: TMode;
  mode: TMode;
  setMode: (mode: TMode) => void;
  children: string;
}) => (
  <div
    className={`box-header-item ${mode === title ? 'active' : ''}`}
    onClick={() => setMode(title)}>
    {children}
  </div>
);

const ParticipantActionRow = ({
  participant,
  party,
  availableActions,
  loadingMap,
  setLoading,
  setSelectedParticipant,
  setMode,
}: {
  participant: IParticipantForUser;
  party: IPartyForUser;
  availableActions: TModeratorActionType[];
  loadingMap: Partial<{ [uid in TUserId]: boolean }>;
  setLoading: (key: any) => void;
  setMode: (mode: TMode) => void;
  setSelectedParticipant: (p: IParticipantForUser | null) => void;
}) => {
  const { user, currentArea, inviteParticipantToArea, fakeUsers, dispatch } = useAppState();

  const isBanned = (participant.tags || []).includes('banned');
  const isModerator = (participant.tags || []).includes('moderator');
  const participantIsHost = (participant.tags || []).includes('host');
  const isVideoMuted = (participant.tags || []).includes('video-muted');
  const isAudioMuted = (participant.tags || []).includes('audio-muted');

  const [moderateParticipant, { loading }] = useMutation(
    gql`
      mutation ModerateParticipant(
        $partyKey: String!
        $participantId: String!
        $moderatorAction: ModeratorAction
      ) {
        moderateParticipant(
          input: {
            partyKey: $partyKey
            participantId: $participantId
            moderatorAction: $moderatorAction
          }
        ) {
          success
        }
      }
    `
  );

  const actionMap: {
    [action in TAvailableActions]: { onClick: () => Promise<any>; text: string };
  } = {
    ban: {
      onClick: () => {
        const fakeUser = fakeUsers.find((p) => p.uid === participant.uid);
        if (fakeUser) {
          const idx = fakeUsers.indexOf(fakeUser);
          const newFakeUsers = [...fakeUsers];
          const newTags = new Set(fakeUser.tags || []);
          newTags.add('banned');
          newFakeUsers[idx] = {
            ...fakeUser,
            tags: Array.from(newTags),
          };
          dispatch({
            type: ActionType.SetFakeUsers,
            fakeUsers: newFakeUsers,
          });
          return Promise.resolve();
        }

        return moderateParticipant({
          variables: {
            participantId: participant.uid,
            partyKey: party.key,
            moderatorAction: {
              type: 'ban',
              value: !isBanned,
            },
          },
        });
      },
      text: isBanned ? 'unban' : 'ban',
    },
    'remove-from-room': {
      onClick: () => {
        const fakeUser = fakeUsers.find((p) => p.uid === participant.uid);
        if (fakeUser) {
          const idx = fakeUsers.indexOf(fakeUser);
          const newFakeUsers = [...fakeUsers];
          const newTags = new Set(fakeUser.tags || []);
          newTags.add('audio-muted');
          newFakeUsers[idx] = {
            ...fakeUser,
            currentArea: `${EAreaPrefix.LobbyPrefix}-0` as TAreaKey,
          };
          dispatch({
            type: ActionType.SetFakeUsers,
            fakeUsers: newFakeUsers,
          });
          return Promise.resolve();
        }

        return moderateParticipant({
          variables: {
            participantId: participant.uid,
            partyKey: party.key,
            moderatorAction: {
              type: 'remove-from-room',
              value: true,
            },
          },
        });
      },
      text: 'remove from room',
    },
    'mute-audio': {
      onClick: () => {
        const fakeUser = fakeUsers.find((p) => p.uid === participant.uid);
        if (fakeUser) {
          const idx = fakeUsers.indexOf(fakeUser);
          const newFakeUsers = [...fakeUsers];
          const newTags = new Set(fakeUser.tags || []);
          newTags.add('audio-muted');
          newFakeUsers[idx] = {
            ...fakeUser,
            tags: Array.from(newTags),
          };
          dispatch({
            type: ActionType.SetFakeUsers,
            fakeUsers: newFakeUsers,
          });
          return Promise.resolve();
        }

        return moderateParticipant({
          variables: {
            participantId: participant.uid,
            partyKey: party.key,
            moderatorAction: {
              type: 'mute-audio',
              value: !isAudioMuted,
            },
          },
        });
      },
      text: isAudioMuted ? 'unmute audio' : 'mute audio',
    },
    'mute-video': {
      onClick: () => {
        const fakeUser = fakeUsers.find((p) => p.uid === participant.uid);
        if (fakeUser) {
          const idx = fakeUsers.indexOf(fakeUser);
          const newFakeUsers = [...fakeUsers];
          const newTags = new Set(fakeUser.tags || []);
          newTags.add('video-muted');
          newFakeUsers[idx] = {
            ...fakeUser,
            tags: Array.from(newTags),
          };
          dispatch({
            type: ActionType.SetFakeUsers,
            fakeUsers: newFakeUsers,
          });
          return Promise.resolve();
        }

        return moderateParticipant({
          variables: {
            participantId: participant.uid,
            partyKey: party.key,
            moderatorAction: {
              type: 'mute-video',
              value: !isVideoMuted,
            },
          },
        });
      },
      text: isVideoMuted ? 'unmute video' : 'mute video',
    },
    'make-host': {
      onClick: () => {
        const fakeUser = fakeUsers.find((p) => p.uid === participant.uid);
        if (fakeUser) {
          const idx = fakeUsers.indexOf(fakeUser);
          const newFakeUsers = [...fakeUsers];
          const newTags = new Set(fakeUser.tags || []);
          newTags.add('host');
          newFakeUsers[idx] = {
            ...fakeUser,
            tags: Array.from(newTags),
          };
          dispatch({
            type: ActionType.SetFakeUsers,
            fakeUsers: newFakeUsers,
          });
          return Promise.resolve();
        }

        return moderateParticipant({
          variables: {
            participantId: participant.uid,
            partyKey: party.key,
            moderatorAction: {
              type: 'make-host',
              value: !participantIsHost,
            },
          },
        });
      },
      text: participantIsHost ? 'remove as host' : 'promote to host',
    },
    'make-moderator': {
      onClick: () => {
        const fakeUser = fakeUsers.find((p) => p.uid === participant.uid);
        if (fakeUser) {
          const idx = fakeUsers.indexOf(fakeUser);
          const newFakeUsers = [...fakeUsers];
          const newTags = new Set(fakeUser.tags || []);
          newTags.add('moderator');
          newFakeUsers[idx] = {
            ...fakeUser,
            tags: Array.from(newTags),
          };
          dispatch({
            type: ActionType.SetFakeUsers,
            fakeUsers: newFakeUsers,
          });
          return Promise.resolve();
        }

        return moderateParticipant({
          variables: {
            participantId: participant.uid,
            partyKey: party.key,
            moderatorAction: {
              type: 'make-moderator',
              value: !isModerator,
            },
          },
        });
      },
      text: isModerator ? 'remove as moderator' : 'promote to moderator',
    },
    spotlight: {
      onClick: () => Promise.resolve(),
      text: 'spotlight',
    },
  };

  return (
    <div className='participant-list-item' key={participant.uid}>
      <div className='participant-list-name'>
        <img
          src={participant.photoURL}
          alt={participant.displayName}
          width={24}
          height={24}
          style={{
            margin: '4px 16px 4px 0',
            objectFit: 'cover',
            borderRadius: '50%',
          }}
        />
        {participant.displayName}
        {isBanned && <div className='user-tag banned'>banned</div>}
        {participantIsHost && <div className='user-tag host'>host</div>}
        {isModerator && <div className='user-tag mod'>mod</div>}
      </div>
      <div className='participant-actions'>
        {participant.uid === user?.uid ? (
          <div />
        ) : loading ? (
          <Spin />
        ) : (
          <>
            {participant.uid !== user?.uid && (
              <Tooltip title={`DM ${participant.displayName}`}>
                <div style={{ marginRight: 10, display: 'flex' }}>
                  <img
                    onClick={() => {
                      setSelectedParticipant(participant);
                      setMode('directmessages');
                    }}
                    alt='DM user'
                    className='invite-user-button'
                    src='/icons/message_private1.svg'></img>
                </div>
              </Tooltip>
            )}
            {!!currentArea && participant.currentArea !== currentArea && !isBanned && (
              <Tooltip title={`Invite ${participant.displayName} to your room`}>
                <div style={{ marginRight: 16, display: 'flex' }}>
                  {loadingMap[participant.uid] ? (
                    <LoadingOutlined style={{ fontSize: 24 }} spin />
                  ) : (
                    <img
                      onClick={async () => {
                        setLoading((lmap: any) => ({
                          ...lmap,
                          [participant.uid]: true,
                        }));
                        try {
                          await inviteParticipantToArea(participant.uid as TUserId);
                          setLoading((lMap: any) => ({
                            ...lMap,
                            [participant.uid]: false,
                          }));
                        } catch (e) {
                          console.log(e);
                          setLoading((lMap: any) => ({
                            ...lMap,
                            [participant.uid]: false,
                          }));
                        }
                      }}
                      alt='invite user'
                      className='invite-user-button'
                      src='/icons/invite_user.svg'></img>
                  )}
                </div>
              </Tooltip>
            )}
            {!!availableActions.length && (
              <div>
                <Dropdown
                  placement='bottomRight'
                  overlay={
                    <Menu>
                      {availableActions.map((action) => (
                        <Menu.Item key={action}>
                          <a onClick={actionMap[action].onClick}>{actionMap[action].text}</a>
                        </Menu.Item>
                      ))}
                    </Menu>
                  }>
                  <Button
                    size='small'
                    type='default'
                    className='accent'
                    icon={<MoreOutlined />}></Button>
                </Dropdown>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const PartyParticipantBox = ({ party }: { party: IPartyForUser }) => {
  const { user, allParticipants, isChatBoxVisible, dispatch } = useAppState();
  const [mode, setMode] = useState<TMode>('chat');
  const [selectedParticipant, setSelectedParticipant] = useState<IParticipantForUser | null>(null);
  const [loadingMap, setLoading] = useState<Partial<{ [uid in TUserId]: boolean }>>({});
  const availableActions = ParticipantActions.getAvailableActions(
    user,
    party?.stage?.participants || {},
    party
  );
  const twitchId =
    party?.stage?.stream?.type === StreamType.Twitch ? party?.stage?.stream?.twitchId : null;

  const twitchEnabled = !!twitchId;

  return (
    <div className={`participant-box-container`}>
      <div className={`participant-box-slider  ${isChatBoxVisible ? '' : 'chat-hide'}`}>
        <div
          className='chat-hide-button'
          onClick={() =>
            dispatch({ type: ActionType.SetChatBoxVisible, isChatBoxVisible: !isChatBoxVisible })
          }>
          {isChatBoxVisible ? (
            <>
              <RightOutlined style={{ paddingBottom: 12 }} />
              <div
                style={{
                  transform: 'rotate(-90deg)',
                  padding: '1em 0.5em',
                  fontWeight: 'bold',
                  color: '#f54783',
                }}>
                hide
              </div>
              <RightOutlined style={{ paddingTop: 12 }} />
            </>
          ) : (
            <>
              <LeftOutlined style={{ paddingBottom: 12 }} />
              <div
                style={{
                  transform: 'rotate(-90deg)',
                  padding: '1em 0.5em',
                  fontWeight: 'bold',
                  color: '#f54783',
                }}>
                chat
              </div>
              <LeftOutlined style={{ paddingTop: 12 }} />
            </>
          )}
        </div>
        <div className={`participant-box-inner  ${isChatBoxVisible ? '' : 'chat-hide'}`}>
          <div className='box-header'>
            <BoxHeader title='chat' mode={mode} setMode={setMode}>
              chat
            </BoxHeader>
            <BoxHeader title='directmessages' mode={mode} setMode={setMode}>
              dm
            </BoxHeader>
            <BoxHeader
              title='guests'
              mode={mode}
              setMode={setMode}>{`${allParticipants.length} guests`}</BoxHeader>
          </div>
          {mode === 'chat' && !twitchEnabled && <PartyMessages party={party} />}
          {twitchEnabled && (
            <div
              style={{
                height: '100%',
                width: '100%',
                display: mode === 'chat' ? 'block' : 'none',
              }}>
              <iframe
                title='Chat'
                frameBorder='no'
                scrolling='yes'
                id='chat_embed'
                src={`https://www.twitch.tv/embed/${twitchId}/chat?darkpopout&parent=backroom.social&parent=localhost&parent=dancefloor-app.web.com`}
                height='100%'
                // width={CHAT_WIDTH}
                style={{
                  position: 'static',
                  height: '100%',
                  width: '100%',
                  // minWidth: CHAT_WIDTH,
                  borderRadius: 20,
                  transition: 'all 0.5s',
                }}></iframe>
            </div>
          )}
          {mode === 'directmessages' && (
            <DirectMessages
              party={party}
              setSelectedParticipant={setSelectedParticipant}
              selectedParticipant={selectedParticipant}
            />
          )}
          {mode === 'guests' && (
            <div className='participant-list'>
              {_.sortBy(allParticipants, (p) => p.displayName).map((p) => (
                <ParticipantActionRow
                  key={p.uid}
                  loadingMap={loadingMap}
                  setLoading={setLoading}
                  party={party}
                  participant={p}
                  availableActions={availableActions}
                  setSelectedParticipant={setSelectedParticipant}
                  setMode={setMode}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartyParticipantBox;
